const HAS_PLAN_INCLUDED_DESCRIPTION = 'com-cobertura-superior'
export default class GA4DataLayerService {
  static getInstance () {
    if (!GA4DataLayerService.instance) {
      GA4DataLayerService.instance = new GA4DataLayerService()
    }

    return GA4DataLayerService.instance
  }

  constructor () {
    this.params = null
    this.activeFilters = []
    this.items = []
  }

  add (data) {
    const dataLayer = window.dataLayer = window.dataLayer || []

    if (data) {
      dataLayer.push(data)
    }
  }

  setActiveFilters (additionalFilter) {
    const enabledFiltersFromStore = window.$nuxt.$store.getters['searchNew/getEnabledFilters'] || []

    const filtersByType = enabledFiltersFromStore.reduce((acc, filter) => {
      if (!acc[filter.type]) {
        acc[filter.type] = []
      }

      acc[filter.type].push({
        id: filter.id,
        name: filter.name
      })
      return acc
    }, {})
    const consolidatedNewFilters = Object.entries(filtersByType).map(([type, items]) => ({
      [type]: items
    }))

    const currentActiveFilters = this.activeFilters || []
    this.activeFilters = currentActiveFilters.filter((activeFilter) => {
      const activeFilterType = Object.keys(activeFilter)[0]

      if (activeFilterType === 'Ordering') {
        return activeFilter
      }
      return filtersByType[activeFilterType]
    })

    const filtersToMerge = [
      additionalFilter || [],
      ...consolidatedNewFilters
    ]

    this.activeFilters = this.mergeAndDeduplicateFilters(
      this.activeFilters || [],
      filtersToMerge
    )
  }

  mergeAndDeduplicateFilters (currentFilters, newFilters) {
    const filterMap = new Map()

    currentFilters.forEach((filter) => {
      const type = filter && Object.keys(filter)[0]
      if (type) {
        filterMap.set(type, filter)
      }
    })

    newFilters.forEach((filter) => {
      const type = filter && Object.keys(filter)[0]
      if (type) {
        filterMap.set(type, filter)
      }
    })
    return Array.from(filterMap.values())
  }

  sendEventWithAllParameters (event, data) {
    const itinerary = this.getItinerary()
    const viewData = this.getViewData()
    const coveragePlan = data?.plan || viewData?.plan
    const planName = coveragePlan ? `plano_${coveragePlan.name.toLowerCase()}` : undefined
    const rentalCover = data?.rentalCover || viewData?.rentalCover
    const hasRentalCover = rentalCover?.included ? HAS_PLAN_INCLUDED_DESCRIPTION : 'sem-cobertura-superior'
    let ga4Items = []

    const plansParams = {
      coverage_plan_type: planName,
      superior_plan: hasRentalCover
    }
    const itemsGa4 = viewData?.GA4?.items || viewData?.GA4 || data?.items
    const vehicleInfo = data?.vehicle ? { vehicle: data.vehicle } : viewData

    if (itemsGa4 && itemsGa4.length > 0) {
      const items = data?.items
        ? data.items[0]
        : (itemsGa4.hasOwnProperty('items')
            ? itemsGa4.items[0]
            : itemsGa4[0])
      const firstItem = {
        ...items,
        ...this.getVehicleSuggestionParams(vehicleInfo),
        ...plansParams,
        item_list_position: undefined
      }
      ga4Items = [firstItem]
    }
    const defaultEcommerceValues = {
      ecommerce: {
        item_list_id: itinerary.pickup.place.id,
        item_list_name: itinerary.pickup.place.name,
        item_list_position: undefined,
        items: ga4Items.map(item => ({
          ...item,
          item_list_id: itinerary.pickup.place.id,
          item_list_name: itinerary.pickup.place.name,
          ...this.getVehicleSuggestionParams(vehicleInfo)
        }))
      }
    }

    const ecommerceParams = data?.ecommerce ? data.ecommerce : defaultEcommerceValues.ecommerce
    const params = this.getLatestParams(planName, plansParams, vehicleInfo, ecommerceParams)

    this.params = params
    this.add({
      event,
      ...this.getDefaultParamsToGA4({}, null),
      ...params,
      ...data?.extra ? { ...data?.extra } : {},
      ...(data?.list_name ? { list_name: data?.list_name } : {})
    })
  }

  getLatestParams (planName, plansParams, viewData, ecommerceParams) {
    const params = {
      ...(planName ? { ...plansParams } : {}),
      ...this.params,
      ...this.getVehicleSuggestionParams(viewData),
      ecommerce: ecommerceParams
    }
    return params
  }

  setParams (params) {
    if (this.params && typeof params === 'object') {
      Object.keys(params).forEach((key) => {
        this.params[key] = params[key]
      })
    } else {
      console.error('Invalid parameters provided for addition.')
    }
  }

  getDefaultParamsToGA4 (params, viewData = null) {
    try {
      const store = window.$nuxt.$store
      const currentCountry = store.getters.getAppResidenceCountry
      const itinerary = this.getItinerary() || window.$nuxt.$store.getters['booking/getBookingPageConfigurePayOnConfigure']?.itinerary
      const pickupCountry = this.parseLocations(this.getPlaceFormatted(itinerary, true)).country.toLowerCase()
      let currentCountryFormatted = localStorage.getItem('currentCountry')
        ? JSON.parse(window.localStorage.getItem('currentCountry')).slug?.toLowerCase()
        : currentCountry?.toLowerCase()
      const userId = store.getters['user/getValidLogin']
        ? store.getters['user/getUserId']
        : undefined

      if (currentCountryFormatted?.includes('brazil')) {
        currentCountryFormatted = 'brasil'
      }
      const currentCountryFormattedName = JSON.parse(window.localStorage.getItem('currentCountry'))?.name?.toLowerCase()
      const isTheSameCountry = currentCountryFormatted?.includes(pickupCountry) || currentCountryFormattedName?.includes(pickupCountry)

      if (this.params && this.params?.user_id) {
        this.params.user_id = userId
      }
      return {
        residence_country: currentCountry,
        pickup_city: this.removeAccents(this.getPlaceByItinerary(itinerary, true).city.ptBr.toLowerCase()),
        pickup_country: this.getPlaceByItinerary(itinerary, true).country.ptBr.toLowerCase(),
        pickup_state: this.getPlaceByItinerary(itinerary, true).state.uf.toLowerCase(),
        dropoff_city: this.removeAccents(this.getPlaceByItinerary(itinerary, false).city.ptBr.toLowerCase()),
        dropoff_country: this.getPlaceByItinerary(itinerary, false).country.ptBr.toLowerCase(),
        dropoff_state: this.getPlaceByItinerary(itinerary, false).state.uf.toLowerCase(),
        pickup_timestamp: new Date(itinerary.pickup.ts).getTime(),
        dropoff_timestamp: new Date(itinerary.dropoff.ts).getTime(),
        pickup_location: this.getPlaceFormatted(itinerary, true),
        dropoff_location: this.getPlaceFormatted(itinerary, false),
        pickup_time: itinerary.pickup.ts,
        dropoff_time: itinerary.dropoff.ts,
        user_id: userId,
        cod_busca: this.getUrlParams(window.$nuxt.$store.getters['booking/getBookingPageConfigurePayOnConfigure']),
        pickup_type: isTheSameCountry ? 'domestic' : 'outbound',
        multistore: this.getMultiStoreSessions(),
        ...params
      }
    } catch (error) {
      console.log('Error getting default GA4 params:', error)
      window.Bugsnag.notify(error)
    }
  }

  getUrlParams (viewData) {
    if (viewData && viewData?.bookingInfo?.url) {
      return viewData.bookingInfo.url
    }

    const searchUrl = window.location.pathname.split('/')
    return searchUrl.length > 3 ? searchUrl[4] : ''
  }

  getItinerary () {
    return window.$nuxt.$store.getters['searchNew/getItinerary'] || window.$nuxt.$store.getters['booking/getBookingPageConfigurePayOnConfigure']?.itinerary
  }

  getViewData () {
    return window.$nuxt.$store.getters['booking/getBookingPageConfigurePayOnConfigure'] || window.$nuxt.$store.getters['booking/getBookingPageConfigurePayOnConfigure']?.formattedBooking
  }

  getItemsToGA4 (viewData) {
    if (this.items && this.items.length > 0) {
      return this.items
    }

    if (viewData?.GA4?.items && viewData?.GA4?.items?.length > 0) {
      return viewData?.GA4?.items
    }

    if (viewData?.GA4) {
      return {
        items: viewData.GA4
      }
    }

    return []
  }

  getVehicleSuggestionParams (viewData) {
    if (!viewData) {
      return {
        vehicle_name: undefined,
        suggestion_vehicle_name: undefined
      }
    }

    const { vehicle, suggestion } = viewData
    const vehicleParams = {
      vehicle_name: vehicle ? vehicle.name : '',
      suggestion_vehicle_name: suggestion ? suggestion.vehicle.name : undefined
    }

    return vehicleParams
  }

  /**
   * Parses a location string into an object containing city, state, and country.
   *
   * @param {string} location - A string representing the location, formatted as "city, state, country".
   * @returns {{ city: string, state: string, country: string }} An object containing the parsed city, state, and country.
   */
  parseLocations (location) {
    const parts = location.split(',').map(part => part.trim())
    const city = parts[0] || ''
    const state = parts.length === 3 ? parts[1] : ''
    const country = parts.length === 3 ? parts[2] : (parts[1] || '')

    return { city, state, country }
  }

  /**
   * Removes accents from a given string.
   *
   * @param {string} string - The input string from which accents will be removed.
   * @returns {string} The input string without accents.
   */
  removeAccents (string) {
    return string.normalize('NFD').replace(/[\u0300-\u036F]/g, '')
  }

  /**
   * Retrieves the place information (city, state, country) based on the itinerary.
   *
   * @param {Object} itinerary - The itinerary object containing pickup and dropoff locations.
   * @param {boolean} isPickup - A flag indicating whether to retrieve the pickup location (true) or dropoff location (false).
   * @returns {Object} An object containing the city, state, and country of the specified location.
   */
  getPlaceByItinerary (itinerary, isPickup) {
    const location = isPickup ? itinerary.pickup : itinerary.dropoff
    const { city, state, country } = location

    return { city, state, country }
  }

  /**
   * Formats the location of a place based on the itinerary.
   *
   * @param {Object} itinerary - The itinerary object containing location details.
   * @param {boolean} isPickup - Indicates whether to format the pickup or dropoff location.
   * @returns {string} The formatted location string in the format "city, state, country".
   *
   * @example
   * const itinerary = {
   *   pickup: { city: { ptBr: 'São Paulo' }, state: { uf: 'SP' }, country: { ptBr: 'Brasil' } },
   *   dropoff: { city: { ptBr: 'Rio de Janeiro' }, state: { uf: 'RJ' }, country: { ptBr: 'Brasil' } }
   * };
   */
  getPlaceFormatted (itinerary, isPickup) {
    const location = isPickup ? itinerary.pickup : itinerary.dropoff
    const { city, state, country } = location
    return `${city.ptBr}, ${state.uf}, ${country.ptBr}`
  }

  getMultiStoreSessions () {
    try {
      const sessionsData = sessionStorage.getItem('multistore_pwa_session')
      if (!sessionsData) return false

      const parsed = JSON.parse(sessionsData)
      const isMultistoreResponse = (parsed && parsed.isMultistoreResponse) || false

      return isMultistoreResponse
    } catch (err) {
      return false
    }
  }
}
